import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import Map, { NavigationControl, ScaleControl } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import { FaChevronDown, FaChevronUp, FaChevronLeft } from "react-icons/fa";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "./Map.css";

import TableResultsComponent from "../TableResults";
import { settings } from "../../config/settings";

const styleOptions = [
  {
    label: "Street",
    style: "mapbox://styles/mapbox/standard",
    drawColor: "#1a73e8",
    thickness: 2,
  },
  {
    label: "Hybrid",
    style: "mapbox://styles/mapbox/satellite-streets-v12",
    drawColor: "#00ff88",
    thickness: 3,
  },
  {
    label: "Satellite",
    style: "mapbox://styles/mapbox/satellite-v9",
    drawColor: "#ffff00",
    thickness: 4,
  },
  {
    label: "Terrain",
    style: "mapbox://styles/mapbox/outdoors-v12",
    drawColor: "#ff3b30",
    thickness: 3,
  },
  {
    label: "Dark",
    style: "mapbox://styles/mapbox/dark-v11",
    drawColor: "#00ffff",
    thickness: 2,
  },
  {
    label: "Light",
    style: "mapbox://styles/mapbox/light-v11",
    drawColor: "#5856d6",
    thickness: 2,
  },
];

const MemoizedMap = React.memo(
  React.forwardRef(
    ({ initialViewState, style, mapStyle, onLoad, onError }, ref) => {
      return (
        <Map
          mapLib={mapboxgl}
          ref={ref}
          initialViewState={initialViewState}
          style={style}
          mapStyle={mapStyle}
          mapboxAccessToken={settings.mapboxKey}
          onLoad={onLoad}
          onError={onError}
        >
          <NavigationControl position="top-left" />
          <ScaleControl position="bottom-left" unit="imperial" />
        </Map>
      );
    },
  ),
);

MemoizedMap.displayName = "MemoizedMap";

MemoizedMap.propTypes = {
  initialViewState: PropTypes.object,
  style: PropTypes.object,
  mapStyle: PropTypes.string,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
};

const MemoizedTableResults = React.memo(
  ({ submittedSearchTerm, path, ssp, token }) => {
    return (
      <TableResultsComponent
        submittedSearchTerm={submittedSearchTerm}
        path={path}
        ssp={ssp}
        token={token}
      />
    );
  },
);

MemoizedTableResults.displayName = "MemoizedTableResults";

MemoizedTableResults.propTypes = {
  submittedSearchTerm: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  path: PropTypes.string,
  ssp: PropTypes.bool,
  token: PropTypes.string,
};

const MapExtComponent = React.memo(
  ({ data, err, error, path, ssp, mapStyleProp, token, onBack }) => {
    const [isResultsExpanded, setIsResultsExpanded] = useState(true);
    const mapRef = useRef(null);
    const [mapStyle, setMapStyle] = useState(
      mapStyleProp || styleOptions[0].style,
    );
    const [polygonColor, setPolygonColor] = useState(
      styleOptions.find(
        (option) => option.style === (mapStyleProp || styleOptions[0].style),
      )?.drawColor || "#00ff00",
    );
    const [isLoading, setIsLoading] = useState(false);
    const [mapError, setMapError] = useState(null);
    const [isMapExpanded, setIsMapExpanded] = useState(true);

    // Surface caller-provided errors in the existing map error UI
    const incomingError = err ?? error;
    useEffect(() => {
      if (incomingError == null || incomingError === "") return;
      setMapError(
        typeof incomingError === "string"
          ? incomingError
          : incomingError.message || String(incomingError),
      );
    }, [incomingError]);

    const mapData = useMemo(() => {
      return data || null;
    }, [data]);

    const encodedDataForAPI = useMemo(() => {
      if (!data) return null;
      try {
        return btoa(JSON.stringify(data)).replaceAll(
          /[/+=]/g,
          (m) => ({ "/": "_", "+": "-", "=": "" })[m],
        );
      } catch (error) {
        console.error("Error encoding data for API:", error);
        return null;
      }
    }, [data]);

    const memoizedDataString = useMemo(() => {
      return encodedDataForAPI;
    }, [encodedDataForAPI]);

    useEffect(() => {
      setTimeout(() => {
        document.querySelector(".map-container");
      }, 500);

      return () => {};
    }, []);

    useEffect(() => {
      if (mapStyleProp) {
        setMapStyle(mapStyleProp);
        const styleOption = styleOptions.find(
          (option) => option.style === mapStyleProp,
        );
        if (styleOption) {
          setPolygonColor(styleOption.drawColor);
        }
      }
    }, [mapStyleProp]);

    const handleMapLoad = useCallback(() => {
      setIsLoading(true);

      if (!mapRef || !mapRef.current) {
        return;
      }

      try {
        const map = mapRef.current.getMap();

        if (mapData) {
          if (
            !mapData.coordinates ||
            !Array.isArray(mapData.coordinates) ||
            mapData.coordinates.length === 0
          ) {
            throw new Error(
              "Invalid data structure: missing or empty coordinates array",
            );
          }

          if (
            !mapData.coordinates[0] ||
            !Array.isArray(mapData.coordinates[0]) ||
            mapData.coordinates[0].length === 0
          ) {
            throw new Error(
              "Invalid data structure: missing or empty first coordinate array",
            );
          }

          const bounds = mapData.coordinates[0].reduce(
            (bounds, coord) => bounds.extend(coord),
            new mapboxgl.LngLatBounds(
              mapData.coordinates[0][0],
              mapData.coordinates[0][0],
            ),
          );

          const expandedBounds = bounds
            .extend([
              bounds.getNorthEast().lng + 0.1,
              bounds.getNorthEast().lat + 0.1,
            ])
            .extend([
              bounds.getSouthWest().lng - 0.1,
              bounds.getSouthWest().lat - 0.1,
            ]);

          map.fitBounds(expandedBounds, {
            padding: 20,
            maxZoom: 15,
            minZoom: 8,
          });

          const geojson = {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: mapData,
                properties: {},
              },
            ],
          };

          if (map.getSource("polygon-source")) {
            map.getSource("polygon-source").setData(geojson);
            map.setPaintProperty("polygon-fill", "fill-color", polygonColor);
            map.setPaintProperty("polygon-outline", "line-color", polygonColor);
          } else {
            map.addSource("polygon-source", {
              type: "geojson",
              data: geojson,
            });

            map.addLayer({
              id: "polygon-fill",
              type: "fill",
              source: "polygon-source",
              paint: {
                "fill-color": polygonColor,
                "fill-opacity": 0.2,
              },
            });

            map.addLayer({
              id: "polygon-outline",
              type: "line",
              source: "polygon-source",
              paint: {
                "line-color": polygonColor,
                "line-width": 2,
              },
            });
          }
        }
      } catch (error) {
        setMapError(error.message);
      } finally {
        setIsLoading(false);
      }
    }, [mapData, polygonColor]);

    const handleMapError = useCallback((e) => {
      setMapError(e.error?.message || "Unknown map error");
    }, []);

    const toggleMapSection = useCallback(() => {
      setIsMapExpanded(!isMapExpanded);
      setTimeout(() => {
        if (mapRef.current) {
          try {
            const map = mapRef.current.getMap();
            map.resize();
          } catch (error) {
            // Ignore resize errors if map is unmounting
            console.error("Map resize failed during toggle:", error);
          }
        }
      }, 300);
    }, [isMapExpanded]);

    const toggleResultsSection = useCallback(() => {
      setIsResultsExpanded(!isResultsExpanded);
    }, [isResultsExpanded]);

    const initialViewState = useMemo(
      () => ({
        longitude: -99.9018,
        latitude: 31.9686,
        zoom: 6,
      }),
      [],
    );

    const mapContainerStyle = useMemo(
      () => ({
        width: "100%",
        height: "100%",
      }),
      [],
    );

    return (
      <div className="search-container-1 map-ext">
        {isLoading && (
          <div className="map-loading-overlay">
            <div className="spinner"></div>
          </div>
        )}

        <div className="content-card global-content-card">
          <div className="card-header">
            <div className="card-title-container">
              <span className="card-title">Map Search</span>
              <button
                className="back-button"
                onClick={onBack}
                aria-label="Go back"
              >
                <FaChevronLeft className="back-icon" />
                <FaChevronLeft
                  className="back-icon"
                  style={{ marginLeft: "-10px" }}
                />
                <FaChevronLeft
                  className="back-icon"
                  style={{ marginLeft: "-10px" }}
                />
              </button>
            </div>
            <div className="accordion-controls">
              <button
                className={`toggle-btn ${isMapExpanded ? "expanded" : "collapsed"}`}
                onClick={toggleMapSection}
                aria-label={isMapExpanded ? "Collapse map" : "Expand map"}
              >
                {isMapExpanded ? (
                  <FaChevronUp className="up-rotate" />
                ) : (
                  <FaChevronDown className="down-rotate" />
                )}
              </button>
            </div>
          </div>
          <div
            className={`card-body ${isMapExpanded ? "expanded" : "collapsed"}`}
          >
            {isMapExpanded && (
              <div
                className="map-container"
                style={{ height: "400px", width: "100%" }}
              >
                {mapError ? (
                  <div className="map-error">
                    <p>Error loading map: {mapError}</p>
                    <button onClick={() => window.location.reload()}>
                      Reload Page
                    </button>
                  </div>
                ) : (
                  <MemoizedMap
                    ref={mapRef}
                    initialViewState={initialViewState}
                    style={mapContainerStyle}
                    mapStyle={mapStyle}
                    onLoad={handleMapLoad}
                    onError={handleMapError}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {mapData && (
          <div className="content-card">
            <div className="card-header">
              <span className="card-title">Results</span>
              <div className="accordion-controls">
                <button
                  className={`toggle-btn ${
                    isResultsExpanded ? "expanded" : "collapsed"
                  }`}
                  onClick={toggleResultsSection}
                  aria-label={
                    isResultsExpanded ? "Collapse results" : "Expand results"
                  }
                >
                  {isResultsExpanded ? (
                    <FaChevronUp className="up-rotate" />
                  ) : (
                    <FaChevronDown className="down-rotate" />
                  )}
                </button>
              </div>
            </div>
            <div
              className={`card-body ${
                isResultsExpanded ? "expanded" : "collapsed"
              }`}
            >
              <MemoizedTableResults
                submittedSearchTerm={memoizedDataString}
                path={path}
                ssp={ssp}
                token={token}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);

MapExtComponent.displayName = "MapExtComponent";

MapExtComponent.propTypes = {
  data: PropTypes.any,
  err: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  path: PropTypes.string,
  ssp: PropTypes.bool,
  mapStyleProp: PropTypes.string,
  token: PropTypes.string,
  onBack: PropTypes.func,
};

export default MapExtComponent;
